import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button, Snackbar, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, Tooltip, Avatar, Checkbox
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, RestaurantMenu as RestaurantIcon, Print as PrintIcon } from '@mui/icons-material';
import { openGuestTokenPrint } from '../../utils/guestTokenPrint';
import PersianDatePicker from '../../components/PersianDatePicker';
import { toPersianNumber } from '../../utils/persianNumber';
import { formatPersianDate } from '../../utils/dateHelper';
import { menuItemsAPI, organizationalUnitsAPI, reservationsAPI, MenuItem as MenuItemType, OrganizationalUnit, getImageUrl } from '../../services/api';

interface GuestReservation {
  id: string;
  createdat: string;
  mealtype: 'breakfast' | 'lunch' | 'dinner';
  iscancelled: boolean;
  meal_date: string;
  firstname: string;
  lastname: string;
  personnelcode: string;
  meal_title: string;
  meal_image: string;
  unit_title: string;
  description?: string;
  isdelivered?: boolean;
  deliveredat?: string | null;
  delivery_code?: string | null;
}

const GuestReservations: React.FC = () => {
  const [reservations, setReservations] = useState<GuestReservation[]>([]);
  const [listLoading, setListLoading] = useState<boolean>(true);
  
  // Modal State
  const [openModal, setOpenModal] = useState<boolean>(false);
  
  // Form State
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [mealType, setMealType] = useState<string>('lunch');
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const [units, setUnits] = useState<OrganizationalUnit[]>([]);
  const [selectedMealId, setSelectedMealId] = useState<string>('');
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  
  // Notifications
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Guest meal tokens: rows picked for printing, and the order just created
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lastCreatedIds, setLastCreatedIds] = useState<string[]>([]);
  const printableIds = reservations.filter((r) => !r.iscancelled).map((r) => r.id);
  const allSelected = printableIds.length > 0 && printableIds.every((id) => selected.has(id));
  const toggleRow = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  useEffect(() => {
    loadUnits();
    loadReservations();
  }, []);

  useEffect(() => {
    if (date && mealType && openModal) {
      loadMenuItems();
    }
  }, [date, mealType, openModal]);

  const loadReservations = async () => {
    try {
      setListLoading(true);
      const data = await reservationsAPI.getGuestReservations();
      setReservations(data);
    } catch (err: any) {
      setError('خطا در دریافت لیست رزروهای مهمان');
    } finally {
      setListLoading(false);
    }
  };

  const loadUnits = async () => {
    try {
      const data = await organizationalUnitsAPI.getOrganizationalUnits();
      setUnits(data);
    } catch (err: any) {
      setError('خطا در دریافت لیست واحدهای سازمانی');
    }
  };

  const loadMenuItems = async () => {
    if (!date) return;
    setLoading(true);
    setSelectedMealId('');
    try {
      const data = await menuItemsAPI.getMenuItems(date, date);
      const filtered = data.filter((m: MenuItemType) => m.mealType === mealType && m.isActive);
      setMenuItems(filtered);
    } catch (err: any) {
      setError('خطا در دریافت منوی غذا');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date || !selectedMealId || !selectedUnitId || quantity < 1) {
      setError('لطفاً تمامی فیلدها را با مقادیر معتبر پر کنید.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const created = await reservationsAPI.createGuestReservations({
        organizationalUnitId: selectedUnitId,
        mealId: selectedMealId,
        mealDate: date,
        mealType: mealType,
        quantity: quantity,
        description: description || undefined
      });
      setSuccess(`${quantity} پرس غذا با موفقیت برای مهمان رزرو شد.`);
      setLastCreatedIds((created.reservations || []).map((r) => r.id));
      setQuantity(1);
      setDescription('');
      setSelectedMealId('');
      setSelectedUnitId('');
      setOpenModal(false);
      loadReservations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در ثبت رزرو مهمان');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelReservation = async (id: string) => {
    if (!window.confirm('آیا از لغو این رزرو مطمئن هستید؟')) return;
    
    try {
      await reservationsAPI.cancelReservation(id);
      setSuccess('رزرو با موفقیت لغو شد');
      loadReservations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در لغو رزرو');
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box sx={{ fontFamily: 'Vazir, sans-serif' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          مدیریت غذای مهمان
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            disabled={selected.size === 0}
            onClick={() => openGuestTokenPrint(Array.from(selected))}
            sx={{ borderRadius: '12px', height: '48px', fontFamily: 'Vazir' }}
          >
            چاپ ژتون{selected.size > 0 ? ` (${toPersianNumber(selected.size)})` : ''}
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={() => setOpenModal(true)}
            sx={{ borderRadius: '12px', height: '48px', fontFamily: 'Vazir' }}
          >
            رزرو جدید
          </Button>
        </Box>
      </Box>

      {lastCreatedIds.length > 0 && (
        <Alert
          severity="success"
          onClose={() => setLastCreatedIds([])}
          sx={{ mb: 3, borderRadius: '16px', alignItems: 'center', fontFamily: 'Vazir' }}
          action={
            <Button
              color="inherit"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => openGuestTokenPrint(lastCreatedIds)}
              sx={{ borderRadius: '12px', height: '48px', fontFamily: 'Vazir', fontWeight: 'bold' }}
            >
              چاپ {toPersianNumber(lastCreatedIds.length)} ژتون
            </Button>
          }
        >
          رزرو مهمان ثبت شد. هر پرس یک ژتون با بارکد دارد که در مانیتور رستوران اسکن و تحویل می‌شود.
        </Alert>
      )}

      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <TableContainer>
          {listLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selected.size > 0 && !allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(printableIds))}
                      inputProps={{ 'aria-label': 'انتخاب همه برای چاپ ژتون' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold', textAlign: 'center' }}>ردیف</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>تاریخ سرو</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>وعده</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>نام غذا</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>توضیحات</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>واحد متقاضی</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold', textAlign: 'center' }}>کد تحویل</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold', textAlign: 'center' }}>وضعیت</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold', textAlign: 'center' }}>زمان ثبت</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold', textAlign: 'center' }}>عملیات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Vazir' }}>
                        هیچ رزرو مهمانی یافت نشد.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  reservations.map((res, index) => (
                    <TableRow key={res.id} hover selected={selected.has(res.id)} sx={{ opacity: res.iscancelled ? 0.6 : 1 }}>
                      <TableCell padding="checkbox">
                        {!res.iscancelled && (
                          <Checkbox
                            checked={selected.has(res.id)}
                            onChange={() => toggleRow(res.id)}
                            inputProps={{ 'aria-label': `انتخاب ردیف ${toPersianNumber(index + 1)} برای چاپ ژتون` }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir', textAlign: 'center' }}>
                        {toPersianNumber(index + 1)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir' }}>
                        {formatPersianDate(res.meal_date)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir' }}>
                        <Chip 
                          size="small" 
                          label={res.mealtype === 'breakfast' ? 'صبحانه' : (res.mealtype === 'lunch' ? 'ناهار' : 'شام')} 
                          color={res.mealtype === 'lunch' ? 'success' : 'primary'} 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            src={res.meal_image ? getImageUrl(res.meal_image) : undefined} 
                            variant="rounded" 
                            sx={{ width: 32, height: 32 }}
                          >
                            <RestaurantIcon />
                          </Avatar>
                          {res.meal_title}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir' }}>
                        {res.description ? (
                          res.description.length > 20 ? (
                            <Tooltip title={res.description}>
                              <Typography variant="body2" sx={{ fontFamily: 'Vazir', cursor: 'help' }}>
                                {res.description.substring(0, 20)}...
                              </Typography>
                            </Tooltip>
                          ) : (
                            res.description
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir' }}>
                        {res.unit_title || res.lastname}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir', textAlign: 'center', whiteSpace: 'nowrap', letterSpacing: '1px' }} dir="ltr">
                        {res.delivery_code ? toPersianNumber(res.delivery_code) : (
                          <Tooltip title="با اولین چاپ ژتون، کد ساخته می‌شود">
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir' }}>—</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir', textAlign: 'center' }}>
                        {res.iscancelled ? (
                          <Chip size="small" label="لغو شده" color="error" />
                        ) : res.isdelivered ? (
                          <Chip
                            size="small"
                            color="info"
                            label={res.deliveredat ? `تحویل شده ${toPersianNumber(formatTime(res.deliveredat))}` : 'تحویل شده'}
                          />
                        ) : (
                          <Chip size="small" label="فعال" color="success" />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir', textAlign: 'center' }}>
                        {toPersianNumber(formatTime(res.createdat))}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {!res.iscancelled && (
                          <Tooltip title="چاپ ژتون">
                            <IconButton color="primary" onClick={() => openGuestTokenPrint([res.id])} aria-label="چاپ ژتون این پرس">
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!res.iscancelled && !res.isdelivered && (
                          <Tooltip title="لغو رزرو">
                            <IconButton color="error" onClick={() => handleCancelReservation(res.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Card>

      {/* New Reservation Modal */}
      <Dialog 
        open={openModal} 
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontFamily: 'Vazir', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
          ثبت رزرو غذای مهمان
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 4 }}>
          <form id="guest-form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: 'Vazir' }}>واحد سازمانی متقاضی</InputLabel>
                <Select
                  value={selectedUnitId}
                  label="واحد سازمانی متقاضی"
                  onChange={(e) => setSelectedUnitId(e.target.value)}
                  sx={{ fontFamily: 'Vazir' }}
                  required
                >
                  {units.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id} sx={{ fontFamily: 'Vazir' }}>
                      {unit.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <PersianDatePicker value={date} onChange={setDate} label="تاریخ رزرو" />

              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: 'Vazir' }}>وعده غذایی</InputLabel>
                <Select
                  value={mealType}
                  label="وعده غذایی"
                  onChange={(e) => setMealType(e.target.value)}
                  sx={{ fontFamily: 'Vazir' }}
                >
                  <MenuItem value="breakfast" sx={{ fontFamily: 'Vazir' }}>صبحانه</MenuItem>
                  <MenuItem value="lunch" sx={{ fontFamily: 'Vazir' }}>ناهار</MenuItem>
                  <MenuItem value="dinner" sx={{ fontFamily: 'Vazir' }}>شام</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={loading}>
                <InputLabel sx={{ fontFamily: 'Vazir' }}>انتخاب غذا</InputLabel>
                <Select
                  value={selectedMealId}
                  label="انتخاب غذا"
                  onChange={(e) => setSelectedMealId(e.target.value)}
                  sx={{ fontFamily: 'Vazir' }}
                  required
                >
                  {loading ? (
                    <MenuItem disabled value="" sx={{ fontFamily: 'Vazir' }}>در حال بارگذاری...</MenuItem>
                  ) : menuItems.length === 0 ? (
                    <MenuItem disabled value="" sx={{ fontFamily: 'Vazir' }}>غذایی برای این تاریخ یافت نشد</MenuItem>
                  ) : (
                    menuItems.map((meal) => {
                      const available = (meal.maxReservations || 0) - (meal.currentReservations || 0);
                      return (
                        <MenuItem key={meal.id} value={meal.mealId} sx={{ fontFamily: 'Vazir', display: 'flex', alignItems: 'center', gap: 1.5 }} disabled={available <= 0}>
                          <Avatar 
                            src={meal.meal?.imageUrl ? getImageUrl(meal.meal?.imageUrl) : undefined} 
                            variant="rounded" 
                            sx={{ width: 32, height: 32 }}
                          >
                            <RestaurantIcon fontSize="small" />
                          </Avatar>
                          {meal.meal?.title} (ظرفیت باقیمانده: {toPersianNumber(available)})
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </FormControl>

              <TextField
                label="تعداد (پرس)"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{ inputProps: { min: 1 } }}
                size="small"
                fullWidth
                sx={{ '& input': { fontFamily: 'Vazir' }, '& label': { fontFamily: 'Vazir' } }}
                required
              />

              <TextField
                label="توضیحات (اختیاری)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
                sx={{ '& textarea': { fontFamily: 'Vazir' }, '& label': { fontFamily: 'Vazir' } }}
              />
            </Box>
          </form>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
          <Button onClick={() => setOpenModal(false)} color="inherit" sx={{ fontFamily: 'Vazir' }}>
            انصراف
          </Button>
          <Button 
            type="submit" 
            form="guest-form" 
            variant="contained" 
            color="primary" 
            disabled={submitting} 
            sx={{ fontFamily: 'Vazir', minWidth: 100 }}
          >
            {submitting ? <CircularProgress size={24} /> : 'ثبت رزرو'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir' }} onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir' }} onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GuestReservations;
